import {
  useState,
  useMemo,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  createContext,
  createRef,
} from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as yup from 'yup';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import isEmpty from 'lodash/isEmpty';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import * as analytics from '@adsk/acs-web-analytics';
import { translate } from '../helpers/i18n';
import { useGetUsersMe } from '../hooks/queries';
import { useGetRfiTypes } from '../hooks/queries/rfiTypesQueries';
import { useGetCustomAttributes } from '../hooks/queries/customAttributesQueries';
import useRfiNotifications from '../hooks/useRfiNotifications';
import { StatusOption, useStatusOptions } from '../hooks/useStatusOptions';
import { createIssue } from '../helpers/analytics-helpers';
import {
  getMandatoryFields,
  rfiCustomIdentifierAllowedCharactersValidator,
} from '../helpers/validation-helpers';
import { ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';
import formatFormValuesForSubmission from './helpers/formatFormValuesForSubmission';
import {
  createSDK,
  RfiPriority,
  RfiStatus,
  RfiType,
  WorkflowType,
  RfiPushpinLocation,
} from '../sdk';
import DataNormalize from '../helpers/dataNormalize';
import { getRfiTypeFieldsInUse, getSelectedTransition } from '../helpers';
import featureFlags from '../helpers/featureFlags';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import { useManageFields, resolveActiveManageFields } from '../hooks/useManageFields';
import { buildManageFieldsValidation } from './helpers/buildManageFieldsValidation';

type RfiFormContextProps = {
  setInvalidIdentifiers: any;
  setPrevSelectedTransition: any;
  setSelectedTransition: Dispatch<SetStateAction<StatusOption | undefined>>;
  createRfi: any;
  defaultValues: any;
  invalidIdentifiers: any;
  isRfiCustomIdentifierVisible: any;
  isDisplayCoReviewers: boolean;
  isSubmitting: boolean;
  mandatoryFields: any;
  prevSelectedTransition: any;
  projectId: any;
  ready: boolean;
  refs: any;
  selectedTransition: StatusOption | undefined;
  showNoWorkflowConfiguration: any;
  validationSchema: any;
};

const serializeRfi = DataNormalize.prototype.serializeRfi.bind(DataNormalize.prototype);

const RfiFormContext = createContext<RfiFormContextProps>({} as RfiFormContextProps);

const useRfiForm = () => {
  const rfiFormCtx = useContextWithValidation(RfiFormContext, {
    hook: 'useRfiForm',
    provider: 'RfiFormProvider',
  });

  return rfiFormCtx;
};

const useRfiFormLogic = ({
  origin,
  featureFlag,
  pushpinLocation,
}: {
  origin: string;
  featureFlag: any;
  pushpinLocation?: RfiPushpinLocation;
}) => {
  const usersMe = useGetUsersMe();
  const projectId = useCurrentProjectId();
  const { data: rfiTypes } = useGetRfiTypes();

  const { showSuccessNotification, showErrorNotification } = useRfiNotifications();

  // Analytics
  const trackCreateRfi = (rfi: any) => {
    const rfiType = rfiTypes?.find(({ id }) => id === rfi.rfiTypeId);
    const isDefaultRfiType = rfiType?.isDefault;
    const rfiTypesFieldsInUse = getRfiTypeFieldsInUse((rfiType || []) as RfiType);
    const numCustomAttributeTypeDefaults = rfiType?.customAttributes?.length || 0;
    const trackingEventObj = createIssue({
      ...rfi,
      origin,
      isDefaultRfiType,
      rfiTypesFieldsInUse,
      numCustomAttributeTypeDefaults,
      hasPushpin: Boolean(pushpinLocation),
    });
    const { eventName, properties } = trackingEventObj;
    analytics.track(eventName, properties);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store invalid custom identifiers from BE.
  const [invalidIdentifiers, setInvalidIdentifiers] = useState<any>([]);

  const [selectedTransition, setSelectedTransition] = useState<StatusOption>();
  const [prevSelectedTransition, setPrevSelectedTransition] = useState<StatusOption>();

  // Set initial transition (e.g - draft, open, openRev1)
  const defaultRfiTypeWfType = rfiTypes?.find(({ isDefault }) => isDefault)?.wfType as WorkflowType;
  const statusOptions = useStatusOptions(defaultRfiTypeWfType);
  useEffect(() => {
    if (statusOptions) {
      const selectedTransition = getSelectedTransition(statusOptions);
      setSelectedTransition(selectedTransition);
    }
  }, [statusOptions]);

  // Show no workflow configuration when selection has no assignees
  const showNoWorkflowConfiguration = isEmpty(selectedTransition?.assigneeUsers);

  // handling customIdentifier visibility state according to selected status
  const isRfiCustomIdentifierVisible = selectedTransition?.serverStatus !== RfiStatus.Draft;

  const isCoReviewersFFEnabled = !!featureFlags.get(FEATURE_FLAGS.CO_REVIEWERS);
  const isDisplayCoReviewers =
    isCoReviewersFFEnabled &&
    (selectedTransition?.permittedAttributes?.some(
      (attr) => attr.name === ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS,
    ) ??
      false);

  // Define validation schema according to selected status
  const mandatoryFields = getMandatoryFields(selectedTransition);
  const isManageFieldsFFEnabled = !!featureFlags.get(FEATURE_FLAGS.LIB_MANAGE_FIELDS);
  const { data: customAttributesDefs } = useGetCustomAttributes();
  const validationSchema = useMemo(() => {
    const baseShape = mandatoryFields.reduce((schema: any, field: any) => {
      switch (field) {
        case 'title':
          return {
            ...schema,
            [field]: yup
              .string()
              .min(1, translate('new.fieldRequired')) // The default is empty string so we need at least 1 character for it to be valid
              .required(translate('new.fieldRequired')),
          };
        case 'assignedTo':
          return {
            ...schema,
            [field]: yup
              .array()
              .of(
                yup.object({
                  id: yup.string().required(),
                  type: yup.string(),
                }),
              )
              .min(1, translate('new.fieldRequired'))
              .required(translate('new.fieldRequired')),
          };
        case 'customIdentifier':
          return {
            ...schema,
            [field]: yup
              .string()
              .test(
                'allowed-characters',
                translate('new.rfiCustomIdentifierAllowedCharactersValidationMessage'),
                rfiCustomIdentifierAllowedCharactersValidator,
              )
              .max(15, translate('new.rfiCustomIdentifierMaxCharactersValidationMessage'))
              .required(translate('new.rfiCustomIdentifierFieldRequired'))
              .test(
                'valid-be-values',
                translate('new.error.customIdentifierNotAvailable'),
                (value) => !invalidIdentifiers.includes(value),
              ),
          };
        default:
          return schema;
      }
    }, {});

    // FF off: keep the static `yup.object(baseShape)` exactly as before. No
    // dependency on form values, no extra renders, byte-identical baseline.
    if (!isManageFieldsFFEnabled) return yup.object(baseShape);

    // FF on: yup.lazy runs its factory on every validation cycle so the
    // mandatory set always reflects the current rfiTypeId + status without
    // requiring a useMemo dependency on formik.values. (The Lazy wrapper
    // itself is memoized; the inner yup.object is rebuilt per validation.)
    return yup.lazy((values: any) => {
      const { map: manageFieldsMap } = resolveActiveManageFields(
        rfiTypes,
        values?.[ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID],
        values?.[ISSUE_ATTRIBUTE_NAMES.STATUS],
      );
      const manageShape = buildManageFieldsValidation({
        manageFieldsMap,
        customAttributesDefs,
        translate,
        isFFEnabled: true,
      });
      // Required-by-status rules come first so they win for the five core
      // fields if buildManageFieldsValidation ever forgets to skip them.
      return yup.object({ ...baseShape, ...manageShape });
    });
  }, [
    invalidIdentifiers,
    mandatoryFields,
    isManageFieldsFFEnabled,
    rfiTypes,
    customAttributesDefs,
  ]);

  // When adding new fields to the form make sure to add them here as well.
  const defaultValues = {
    [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: [],
    [ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER]: '',
    [ISSUE_ATTRIBUTE_NAMES.QUESTION]: '',
    [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: '',
    [ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION]: '',
    [ISSUE_ATTRIBUTE_NAMES.LOCATION_IDS]: [],
    [ISSUE_ATTRIBUTE_NAMES.PRIORITY]: RfiPriority.Normal,
    [ISSUE_ATTRIBUTE_NAMES.STATUS]: selectedTransition?.serverStatus,
    [ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER]: '',
    [ISSUE_ATTRIBUTE_NAMES.TITLE]: '',
    [ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID]: '',
    [ISSUE_ATTRIBUTE_NAMES.WATCHERS]: [],
    [ISSUE_ATTRIBUTE_NAMES.CO_REVIEWERS]: [],
    [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: [],
    [ISSUE_ATTRIBUTE_NAMES.COST]: null,
    [ISSUE_ATTRIBUTE_NAMES.SCHEDULE]: null,
    [ISSUE_ATTRIBUTE_NAMES.DISCIPLINE]: [],
    [ISSUE_ATTRIBUTE_NAMES.CATEGORY]: [],
    [ISSUE_ATTRIBUTE_NAMES.SUB_ID]: '',
  };

  if (usersMe.isFetching || !selectedTransition) {
    return { ready: false, defaultValues };
  }

  // define refs for fields, used to scroll into view on error
  const refs: any = {};
  Object.keys(defaultValues).forEach((field) => {
    refs[field] = createRef();
  });

  const createRfi = async (values: any, formik: any, dueDateTimeZone: string | undefined) => {
    setIsSubmitting(true);
    try {
      const sdk = createSDK({ projectId } as any);
      // Defense-in-depth payload stripping: derive the hidden lists from the
      // active RFI type at submit time and forward them to the formatter so
      // any standard or custom value that survived `clearHiddenFieldValues`
      // is dropped before reaching the BE. Skipped when the FF is off so the
      // submission path stays byte-identical to the legacy behavior.
      const { hiddenStandardNames, hiddenCustomIds } = isManageFieldsFFEnabled
        ? resolveActiveManageFields(
            rfiTypes,
            values[ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID],
            values[ISSUE_ATTRIBUTE_NAMES.STATUS],
          )
        : { hiddenStandardNames: [], hiddenCustomIds: [] };
      // TODO - SCPJM-8072 - combine these two functions to one
      const formattedFormValues = formatFormValuesForSubmission(values, {
        isRfiCustomIdentifierVisible,
        isDisplayCoReviewers,
        hiddenStandardNames,
        hiddenCustomIds,
      });
      const serializedRfi = serializeRfi(formattedFormValues, { dueDateTimeZone });
      const newRfi = await sdk.RFIs.create({
        ...serializedRfi,
        ...(pushpinLocation && { placements: pushpinLocation.placements }),
      });
      if (featureFlag.notifications) {
        showSuccessNotification(newRfi);
      }
      if (featureFlag.analytics) {
        trackCreateRfi(newRfi);
      }
      setIsSubmitting(false);
      formik.resetForm();
      return newRfi;
    } catch (err) {
      if (featureFlag.notifications) {
        showErrorNotification();
      }
      setIsSubmitting(false);
      return false;
    }
  };

  return {
    setInvalidIdentifiers,
    setPrevSelectedTransition,
    setSelectedTransition,
    createRfi,
    defaultValues,
    invalidIdentifiers,
    isRfiCustomIdentifierVisible,
    isDisplayCoReviewers,
    isSubmitting,
    mandatoryFields,
    prevSelectedTransition,
    projectId,
    ready: true,
    refs,
    selectedTransition,
    showNoWorkflowConfiguration,
    validationSchema,
  };
};
const RfiFormProvider = ({
  children,
  origin,
  featureFlag,
  pushpinLocation,
}: {
  children: ReactNode;
  origin: string;
  featureFlag?: any;
  pushpinLocation?: RfiPushpinLocation;
}): ReactNode => {
  const ctx = useRfiFormLogic({ origin, featureFlag, pushpinLocation });
  return <RfiFormContext.Provider value={ctx as any}>{children}</RfiFormContext.Provider>;
};
RfiFormProvider.propTypes = {
  /**
   * The children to render inside
   */
  children: PropTypes.node.isRequired,
  /**
   * where this rfi originated from, this is for analytics, e.g 'Issues`
   */
  origin: PropTypes.string,
  featureFlag: PropTypes.shape({
    notifications: PropTypes.bool,
  }),
  /**
   * The pushpin location of the RFI, this is for markups
   */
  pushpinLocation: PropTypes.shape({
    placements: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        sheetId: PropTypes.string,
        viewable: PropTypes.shape({
          guid: PropTypes.string,
          name: PropTypes.string,
        }),
        pins: PropTypes.arrayOf(
          PropTypes.shape({
            position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
            z: PropTypes.number,
          }),
        ),
      }),
    ),
  }),
};

RfiFormProvider.defaultProps = {
  origin: 'NOT SET',
  featureFlag: {
    analytics: true,
    notifications: true,
  },
};

/**
 * Form-aware wrapper around `useManageFields` for use INSIDE the Formik tree.
 *
 * Resolves the active manage-fields map by reading the form's current
 * `rfiTypeId` and `status`, then delegates to `useManageFields` (which also
 * handles the FF-off no-op and the Draft → default-type swap).
 *
 * Use this hook in `RfiForm.js`, `CustomAttributesFields.tsx`, and any other
 * field component that needs to read visibility state. Outside Formik (e.g.
 * in `RfiCreateForm.jsx`), keep using `useRfiForm()`.
 */
const useFormManageFields = () => {
  const formik = useFormikContext<{
    [ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID]?: string;
    [ISSUE_ATTRIBUTE_NAMES.STATUS]?: RfiStatus;
  }>();
  const rfiTypeId = formik.values?.[ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID];
  const status = formik.values?.[ISSUE_ATTRIBUTE_NAMES.STATUS];
  return useManageFields(rfiTypeId, status);
};

/**
 * One-stop hook for Create-RFI standard-field components. Collapses the
 * boilerplate every field needs:
 *
 * - `isFieldsMandatory`: status-driven `mandatoryFields` OR the new
 *   manageFields-driven `isMandatory` selector. The five
 *   `CONSTANT_MANDATORY_FIELDS` always fall through to `mandatoryFields`
 *   because `useFormManageFields().isMandatory` returns `false` for them.
 *   With the LIB_MANAGE_FIELDS FF off, the manage-fields term is always
 *   `false`, so this matches the legacy `mandatoryFields.includes(name)`
 *   answer exactly.
 * - `errorMessage`: gated on `touched OR submitCount > 0`. Submit-time
 *   auto-touch only flows to keys present in `formik.values`, so any
 *   standard field must be seeded into `defaultValues` below — otherwise
 *   it has no Formik-tracked value, no ref for `ScrollErrorToView` to
 *   scroll to, and relies on `submitCount > 0` alone to surface its error.
 * - `isSubmitting` and `fieldRef` from the form context, ready to spread.
 *
 * Components that also read `formik.values[fieldName]` or call
 * `formik.setFieldValue` keep their own `useFormikContext()` alongside.
 */
const useRfiFormField = (fieldName: string) => {
  const formik = useFormikContext<Record<string, unknown>>();
  const { mandatoryFields, isSubmitting, refs } = useRfiForm();
  const { isMandatory } = useFormManageFields();
  const isFieldsMandatory = mandatoryFields.includes(fieldName) || isMandatory(fieldName);
  const errorMessage =
    (formik.touched[fieldName] || formik.submitCount > 0) && formik.errors[fieldName];
  return { isFieldsMandatory, errorMessage, isSubmitting, fieldRef: refs[fieldName] };
};

export { RfiFormContext, useRfiForm, RfiFormProvider, useFormManageFields, useRfiFormField };
export default RfiFormProvider;
